import { debounce } from '@material-ui/core';
import { WMTableRef } from '@walkme/wm-ui';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AudienceRuleGraphql,
    CalculateRecipientsListDocument,
    CalculateRecipientsListQuery,
    CalculateRecipientsListQueryVariables,
    QueryCalculateRecipientsListArgs,
    RecipientsFilter,
    UserRecipientGraphql
} from '../../../../../../generated/apollo-hooks';
import { useIsMountedRef } from '../../../../../helpers/useIsMounted';
import useSelector from '../../../../../infrastructure/useSelector';
import { recipientsSearchMinSymbols } from '../../../consts';
import { ApolloClient, useApolloClient } from '../../../../../infrastructure/configureApolloClient';
import { Subscription } from 'zen-observable-ts';

const initialSearchValue = { page: 0, search: '', limit: 50, recipientsFilter: RecipientsFilter.Active };

async function makeGqlQuery(
    subscription: MutableRefObject<Subscription | null>,
    apolloClient: ApolloClient,
    variables: QueryCalculateRecipientsListArgs
) {
    return new Promise<CalculateRecipientsListQuery>((resolve, reject) => {
        if (subscription.current) {
            subscription.current.unsubscribe();
        }

        subscription.current = apolloClient
            .watchQuery<CalculateRecipientsListQuery, CalculateRecipientsListQueryVariables>({
                query: CalculateRecipientsListDocument,
                variables
            })
            .subscribe(
                data => resolve(data.data),
                error => reject(error)
            );
    });
}

export const useCalcRecipientsList = ({ rules = [] }: { rules: AudienceRuleGraphql[] }) => {
    const systemId = useSelector(store => store.common.currentSystem);
    const tableRef = useRef<WMTableRef>(null);
    const [query, setQuery] = useState(initialSearchValue);
    const [recipientsList, setRecipientsList] = useState<UserRecipientGraphql[]>([]);
    const [error, setError] = useState<Error | unknown | null>(null);
    const [recipientsCountResult, setRecipientsCountResult] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const clientSideFiltered = useMemo(() => recipientsList.filter(it => it.label.startsWith(query.search)), [query.search, recipientsList]);
    const apolloClient = useApolloClient();

    const subscription = useRef<Subscription | null>(null);

    useEffect(
        () => () => {
            if (subscription.current) {
                subscription.current?.unsubscribe?.();
                subscription.current = null;
            }
        },
        []
    );

    const fetchRecipients = useCallback(async (apolloClient: ApolloClient, variables: QueryCalculateRecipientsListArgs) => {
        setError(null);

        if (!variables.rules || !variables?.rules.length) {
            setRecipientsCountResult(0);
            setRecipientsList([]);
            setIsLoading(false);
            setIsLoadingMore(false);
            return;
        }

        if (variables.query?.page === 0) {
            tableRef.current?.scrollToRow(0, 'start');
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const data = (await makeGqlQuery(subscription, apolloClient, variables))?.calculateRecipientsList;

            if (data) {
                setRecipientsCountResult(data.count);
                if (Array.isArray(data.recipients)) {
                    setRecipientsList(prev => {
                        return variables.query?.page === 0 ? [...data.recipients] : [...prev, ...data.recipients];
                    });
                }
            }
        } catch (err: Error | unknown) {
            setError(err);
        }

        setIsLoading(false);
        setIsLoadingMore(false);
    }, []);

    const retry = () => fetchRecipients(apolloClient, { query, rules, systemId });

    const loadNext = () => {
        setQuery({ ...query, page: query.page + 1 });
    };

    useEffect(() => {
        fetchRecipients(apolloClient, { query, rules, systemId });
    }, [query, apolloClient, systemId, rules, fetchRecipients]);

    const isMountedRef = useIsMountedRef();
    const onSearch = useMemo(
        () =>
            debounce(async (term: string) => {
                if (!isMountedRef.current) {
                    return;
                }
                if (term.length >= recipientsSearchMinSymbols) {
                    setQuery(_query => ({ ..._query, search: term, page: 0 }));
                } else {
                    setQuery(initialSearchValue);
                }
            }, 500),
        [isMountedRef]
    );

    const fetchNextPage = () => {
        if (!isLoading && !isLoadingMore && recipientsList.length < recipientsCountResult) {
            loadNext();
        }
    };
    return {
        isLoading,
        isLoadingMore,
        recipientsList,
        error,
        fetchNextPage,
        onSearch,
        tableRef,
        fetchRecipients,
        retry,
        clientSideFiltered,
        query,
        setQuery,
        recipientsCountResult
    };
};
